import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type Site = components['schemas']['Site'];
type CreateSiteRequest = components['schemas']['CreateSiteRequest'];

interface AddSiteModalProps {
  onClose: () => void;
  onCreated?: (site: Site) => void;
}

const MAX_NAME = 255;
const MAX_URL = 2048;

export function AddSiteModal({ onClose, onCreated }: AddSiteModalProps) {
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://');
  const [maxPages, setMaxPages] = useState<string>('');
  const [scanFrequencyHours, setScanFrequencyHours] = useState<string>('');
  const [clientError, setClientError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: async (body: CreateSiteRequest): Promise<Site> => {
      const { data, error } = await apiClient.POST('/sites', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: (site) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
      onCreated?.(site);
      onClose();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setClientError(null);

    const trimmedName = name.trim();
    const trimmedUrl = baseUrl.trim();
    if (!trimmedName) return setClientError('Name is required.');
    if (!trimmedUrl.startsWith('https://')) {
      return setClientError('Base URL must start with https://');
    }

    const settings: CreateSiteRequest['settings'] = {};
    if (maxPages) {
      const n = Number.parseInt(maxPages, 10);
      if (!Number.isFinite(n) || n < 1 || n > 10000) {
        return setClientError('Max pages must be between 1 and 10000.');
      }
      settings.maxPages = n;
    }
    if (scanFrequencyHours) {
      const n = Number.parseInt(scanFrequencyHours, 10);
      if (!Number.isFinite(n) || n < 1 || n > 8760) {
        return setClientError('Scan frequency must be between 1 and 8760 hours.');
      }
      settings.scanFrequencyHours = n;
    }

    const body: CreateSiteRequest = {
      name: trimmedName,
      baseUrl: trimmedUrl,
      ...(Object.keys(settings).length > 0 ? { settings } : {}),
    };
    mutation.mutate(body);
  }

  const serverError = mutation.isError ? describeError(mutation.error) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      data-testid="add-site-modal"
      onClick={onClose}
    >
      <form
        className="flex w-full max-w-md flex-col gap-3 rounded-md border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-base font-semibold">Add site</h2>
        <p className="text-xs text-muted-foreground">
          The server validates the URL against SSRF rules — private/loopback hosts and
          non-HTTPS schemes are rejected.
        </p>

        <label className="flex flex-col gap-1 text-xs font-medium">
          Name
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
            placeholder="Hillsdale admissions"
            maxLength={MAX_NAME}
            autoFocus
            data-testid="add-site-name"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium">
          Base URL
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value.slice(0, MAX_URL))}
            placeholder="https://example.com"
            maxLength={MAX_URL}
            inputMode="url"
            data-testid="add-site-base-url"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs font-medium">
            Max pages
            <Input
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="25"
              inputMode="numeric"
              data-testid="add-site-max-pages"
            />
            <span className="text-[10px] text-muted-foreground">Default 25; up to 10000.</span>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium">
            Scan every (hours)
            <Input
              value={scanFrequencyHours}
              onChange={(e) =>
                setScanFrequencyHours(e.target.value.replace(/[^0-9]/g, ''))
              }
              placeholder="24"
              inputMode="numeric"
              data-testid="add-site-scan-frequency"
            />
            <span className="text-[10px] text-muted-foreground">Default 24h; up to 8760.</span>
          </label>
        </div>

        {(clientError || serverError) && (
          <div className="rounded-sm border border-rose-300 bg-rose-50 p-2 text-xs text-rose-900">
            {clientError ?? serverError}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={mutation.isPending}
            data-testid="add-site-confirm"
          >
            {mutation.isPending ? 'Creating…' : 'Create site'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function describeError(err: unknown): string {
  if (err && typeof err === 'object' && 'error' in err) {
    const inner = (err as { error?: { code?: string; message?: string } }).error;
    if (inner?.code === 'SSRF_RISK') {
      return 'Base URL was rejected as an SSRF risk (private/loopback/link-local host, or non-HTTPS scheme).';
    }
    if (inner?.code === 'VALIDATION_ERROR') {
      return 'Request failed validation. Check the name and URL.';
    }
    if (inner?.message) return inner.message;
  }
  return 'Failed to create site. You may lack the `sites:write` permission.';
}
